import { get, post, put, del, patch } from '@/services/http';
import { EventBus } from '@/eventBus';
import Collection from '@/collections/collection';
import type {
  PurchaseListItem,
  PurchaseListItemInput,
  PurchaseListGroupedResponse,
  PurchaseListGroup,
} from '@/types/purchase-list';

export const PURCHASE_LIST_UPDATED = 'purchase-list-updated';

export class PurchaseList extends Collection<PurchaseListItem> {
  public groups: Record<string, PurchaseListGroup> = {};
  public totalCount = 0;

  public async fetch(): Promise<void> {
    const resp: any = await get('/purchase-list');
    const data = resp.data as PurchaseListGroupedResponse;
    this.groups = data.groups || {};
    this.totalCount = data.totalCount ?? 0;
  }

  public async getCount(): Promise<number> {
    const resp: any = await get('/purchase-list/count');
    return resp.data?.count ?? 0;
  }

  public async addItem(input: PurchaseListItemInput): Promise<PurchaseListItem> {
    const item = await this.addItemInternal(input);
    EventBus.$emit(PURCHASE_LIST_UPDATED);
    return item;
  }

  public async addItems(inputs: PurchaseListItemInput[]): Promise<{
    added: PurchaseListItem[];
    failedCount: number;
  }> {
    if (!inputs || inputs.length === 0) {
      return { added: [], failedCount: 0 };
    }
    const resp: any = await post('/purchase-list/items/bulk', { items: inputs });
    const added = (resp.data?.added ?? []) as PurchaseListItem[];
    const failedCount = resp.data?.failedCount ?? (resp.data?.errors?.length ?? 0);
    if (added.length > 0) {
      EventBus.$emit(PURCHASE_LIST_UPDATED);
    }
    return { added, failedCount };
  }

  private async addItemInternal(input: PurchaseListItemInput): Promise<PurchaseListItem> {
    const resp: any = await post('/purchase-list/items', input);
    return resp.data.item as PurchaseListItem;
  }

  public async updateItem(id: number, changes: Partial<PurchaseListItem>): Promise<PurchaseListItem> {
    const resp: any = await put(`/purchase-list/items/${id}`, changes);
    EventBus.$emit(PURCHASE_LIST_UPDATED);
    return resp.data.item as PurchaseListItem;
  }

  public async assignSupplier(id: number, supplierId: number, supplierName: string): Promise<PurchaseListItem> {
    const resp: any = await patch(`/purchase-list/items/${id}/assign-supplier`, { supplierId, supplierName });
    EventBus.$emit(PURCHASE_LIST_UPDATED);
    return resp.data.item as PurchaseListItem;
  }

  public async removeItem(id: number): Promise<void> {
    await del(`/purchase-list/items/${id}`);
    EventBus.$emit(PURCHASE_LIST_UPDATED);
  }

  public async clearSupplier(supplierId: number | 'unassigned'): Promise<void> {
    await del(`/purchase-list/supplier/${supplierId}`);
    EventBus.$emit(PURCHASE_LIST_UPDATED);
  }

  public async clearAll(): Promise<void> {
    await del('/purchase-list');
    EventBus.$emit(PURCHASE_LIST_UPDATED);
  }
}

export const purchaseList = new PurchaseList();
